import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { authApi } from '@/lib/api/auth'
import { useAuthStore } from '@/stores'
import type { UpdateProfileRequest } from '@/types'

const QUERY_KEYS = {
  profile: ['profile'] as const,
}

export function useProfile() {
  const queryClient = useQueryClient()
  const { setUser } = useAuthStore()

  const profileQuery = useQuery({
    queryKey: QUERY_KEYS.profile,
    queryFn: authApi.getProfile,
    staleTime: 5 * 60 * 1000, // 5 minutes
  })

  const updateProfileMutation = useMutation({
    mutationFn: (data: UpdateProfileRequest) => authApi.updateProfile(data),
    onSuccess: (updatedUser) => {
      // Update the query cache
      queryClient.setQueryData(QUERY_KEYS.profile, updatedUser)
      // Update the auth store
      setUser(updatedUser)
    },
  })

  const deleteAccountMutation = useMutation({
    mutationFn: authApi.deleteAccount,
    onSuccess: () => {
      // Clear all queries
      queryClient.clear()
    },
  })

  return {
    profile: profileQuery.data,
    isLoading: profileQuery.isLoading,
    error: profileQuery.error,
    refetch: profileQuery.refetch,

    updateProfile: updateProfileMutation.mutateAsync,
    isUpdating: updateProfileMutation.isPending,
    updateError: updateProfileMutation.error,

    deleteAccount: deleteAccountMutation.mutateAsync,
    isDeleting: deleteAccountMutation.isPending,
    deleteError: deleteAccountMutation.error,
  }
}
